import { useEffect, useMemo } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import Container from '../../components/Container/Container'
import CategoriesCatalog from '../../components/section/catalog/CategoriesCatalog/CategoriesCatalog'
import GridProducts from '../../components/section/catalog/GridProducts/GridProducts'
import Breadcrumbs from '../../components/ui/Breadcrumbs/Breadcrumbs'
import ErrorWrapper from '../../components/ui/ErrorWrapper/ErrorWrapper'
import { useCategoriesRedux } from '../../hooks/useCategoriesRedux'
import { usePageMeta } from '../../hooks/usePageMeta'
import { shopDescription } from '../../utils/pageMeta'
import { useProducts } from '../../hooks/useProducts'
import { analytics, productToEventItem } from '../../analytics'
import { getMinEffectivePrice } from '../../utils/cartUtils'
import {
  buildShopSearch,
  parseShopSearch,
  ShopFilters,
  ShopSortOption,
} from '../../utils/shopQuery'
import s from './ShopPage.module.css'

export default function ShopPage() {
  const { category, tag } = useParams()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const { getCategoryByInternalName, categories } = useCategoriesRedux()

  const isSales = category === 'sales'
  const categoryData = !isSales && !tag && category ? getCategoryByInternalName(category) : null
  const selectedCategory = isSales ? 'sales' : category || null

  const { filters: activeFilters, sort: sortOption } = useMemo(
    () => parseShopSearch(searchParams.toString()),
    [searchParams],
  )

  const setActiveFilters = (next: ShopFilters) => {
    setSearchParams(buildShopSearch(next, sortOption), { replace: true })
  }

  const setSortOption = (next: ShopSortOption) => {
    setSearchParams(buildShopSearch(activeFilters, next), { replace: true })
  }

  const { products, loading, filterSchema } = useProducts({ tagFilter: tag })

  const modifiedFilterSchema = useMemo(() => {
    if (!filterSchema) return filterSchema

    const effectiveCategory = selectedCategory || categoryData?.internalName || null

    let modifiedSchema = { ...filterSchema }

    if (effectiveCategory !== 'dripi' && effectiveCategory !== 'drip') {
      const { dripWeight, ...schemaWithoutDripWeight } = modifiedSchema
      modifiedSchema = schemaWithoutDripWeight
    }

    const { packageProductType, grindType, ...schemaWithoutProductTypeAndGrindType } = modifiedSchema
    modifiedSchema = schemaWithoutProductTypeAndGrindType

    return modifiedSchema
  }, [filterSchema, selectedCategory, categoryData])

  const filteredProducts = useMemo(() => {
    let list = products

    if (isSales) {
      list = list.filter(p => (p.prices || []).some(v => v.discount > 0))
    } else if (!tag) {
      const effectiveCategory = selectedCategory || categoryData?.internalName || null
      if (effectiveCategory) {
        list = list.filter(p => p.categoryInternalName === effectiveCategory)
      }
    }

    Object.entries(activeFilters).forEach(([key, val]) => {
      if (!Array.isArray(val)) return

      const isRange = val.length === 2 && typeof val[0] === 'number' && typeof val[1] === 'number'
      if (isRange) {
        const [min, max] = val as [number, number]
        if (key === 'price') {
          list = list.filter(p => {
            const minPrice = getMinEffectivePrice(p.prices || [])
            if (minPrice === null) return false
            return minPrice >= min && minPrice <= max
          })
        } else if (key === 'scaScore') {
          list = list.filter(
            p => typeof p.scaScore === 'number' && p.scaScore >= min && p.scaScore <= max,
          )
        } else if (key === 'sweetness') {
          list = list.filter(
            p =>
              p.sweetness !== null &&
              typeof p.sweetness === 'number' &&
              p.sweetness >= min &&
              p.sweetness <= max,
          )
        } else if (key === 'acidity') {
          list = list.filter(
            p =>
              p.acidity !== null &&
              typeof p.acidity === 'number' &&
              p.acidity >= min &&
              p.acidity <= max,
          )
        } else if (key === 'bitterness') {
          list = list.filter(
            p =>
              p.bitterness !== null &&
              typeof p.bitterness === 'number' &&
              p.bitterness >= min &&
              p.bitterness <= max,
          )
        }
      } else {
        const selected = val as string[]
        if (selected.length === 0) return

        if (key === 'beanType') {
          list = list.filter(p => {
            if (!p.beanType || !p.beanType.value) return false
            return selected.includes(p.beanType.value)
          })
        } else if (key === 'processingMethod') {
          list = list.filter(p => {
            if (!p.processingMethod || !p.processingMethod.value) return false
            return selected.includes(p.processingMethod.value)
          })
        } else if (key === 'originCountry' || key === 'countries') {
          list = list.filter(p => {
            if (!p.originCountry) return false
            return selected.includes(p.originCountry)
          })
        } else if (key === 'beanVariety') {
          list = list.filter(p => {
            if (!p.beanVariety || !p.beanVariety.value) return false
            return selected.includes(p.beanVariety.value)
          })
        } else if (key === 'roastType') {
          list = list.filter(p => {
            if (!p.roastType || !p.roastType.value) return false
            return selected.includes(p.roastType.value)
          })
        } else if (key === 'tags') {
          list = list.filter(p => {
            if (!p.tags || p.tags.length === 0) return false
            return p.tags.some(item => selected.includes(item.name))
          })
        } else if (key === 'packageWeight' || key === 'dripWeight') {
          list = list.filter(p =>
            (p.prices || []).some(v => v.weight && selected.includes(v.weight)),
          )
        }
      }
    })

    const sortedList = [...list].sort((a, b) => {
      const priceA = getMinEffectivePrice(a.prices || []) ?? Infinity
      const priceB = getMinEffectivePrice(b.prices || []) ?? Infinity
      switch (sortOption) {
        case 'price-asc':
          return priceA - priceB
        case 'price-desc':
          return priceB - priceA
        case 'newest':
          return new Date(b.roastDate).getTime() - new Date(a.roastDate).getTime()
        case 'popularity':
        default:
          return (a.displayOrder ?? Infinity) - (b.displayOrder ?? Infinity)
      }
    })

    return sortedList
  }, [products, activeFilters, selectedCategory, categoryData?.internalName, sortOption, isSales, tag])

  useEffect(() => {
    const categoryName = category || 'all'
    if (filteredProducts.length > 0) {
      analytics.track('view_item_list', {
        list_id: categoryName,
        list_name: categoryName,
        items: filteredProducts.slice(0, 10).map(p => productToEventItem(p)),
      })
    }
  }, [category, filteredProducts])

  const categoryOptions = useMemo(
    () => [
      ...categories.map(c => ({
        value: c.internalName,
        label: c.externalName,
      })),
    ],
    [categories],
  )

  const handleCategoryChange = (newCategory: string | null) => {
    if (newCategory === null || newCategory === 'sales') {
      navigate('/shop')
      return
    }
    navigate(`/shop/${newCategory}`)
  }

  const getTitle = () => {
    if (tag) {
      return tag
    }
    if (isSales) {
      return 'Каталог'
    }
    if (categoryData) {
      return categoryData.externalName
    }
    return 'Каталог'
  }

  const shopTitle = getTitle()
  const shopPath = tag ? `/shop/tag/${tag}` : category ? `/shop/${category}` : '/shop'
  usePageMeta({
    title: shopTitle,
    description: shopDescription(shopTitle, categoryData?.description),
    path: shopPath,
  })

  return (
    <Container>
      <Breadcrumbs
        categoryName={categoryData?.externalName}
        categoryInternalName={categoryData?.internalName}
      />
      <h1 className={s.name}>{getTitle()}</h1>
      {!categoryData && !tag && <CategoriesCatalog />}
      <ErrorWrapper section="Каталог товарів">
        <GridProducts
          products={filteredProducts}
          loading={loading}
          schema={modifiedFilterSchema}
          onFiltersChange={setActiveFilters}
          categoryOptions={categoryOptions}
          initialCategory={selectedCategory}
          onCategoryChange={handleCategoryChange}
          initialFilters={activeFilters}
          allProductsForRanges={products}
          onSortChange={setSortOption}
          initialSort={sortOption}
        />
      </ErrorWrapper>
    </Container>
  )
}
